/**
 * A list filter as a step's input (Feature 77 D8, D25): the list it reads,
 * the conditions an item is kept by, and what it hands on, whole items or
 * one field of each. Below it, what the filter yields against the field it
 * fills, in the agent's own type families (D26); the agent's check decides
 * the save. An emptied condition list is left out rather than sent empty,
 * which the agent refuses; with whole items and no condition the filter is
 * the list itself and saves as a plain reference (D9).
 */
import type {
  FieldSpec,
  LinkageItemRules,
  LinkageJoin,
  LinkageListFilter,
  LinkageRule,
} from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Stack, Typography } from '../../ui/finixt.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Select } from '../../ui/Select.js';
import { ITEM, itemCondition, typesAgree } from './lists.js';
import { storedOption } from './positions.js';
import { PositionHint } from './PositionHint.js';
import { PICKER_WIDTH, pickerOptions, type ReferenceOption } from './references.js';
import { RuleRow } from './RuleRow.js';

/** The filter with these conditions; none at all leaves `where` out. */
function withWhere(source: LinkageListFilter, items: LinkageRule[], join: LinkageJoin) {
  const { where: _dropped, ...rest } = source;
  const where: LinkageItemRules = { join, items };
  return items.length > 0 ? { ...rest, where } : rest;
}

/** The filter handing on whole items, or one field of each. */
function withPick(source: LinkageListFilter, pick: string | undefined): LinkageListFilter {
  const { pick: _dropped, ...rest } = source;
  return pick === undefined ? rest : { ...rest, pick };
}

/** "Yields a list of string: fits ids", and whether it fits. */
function yieldText(field: FieldSpec, given: string): { text: string; fits: boolean } {
  const wanted = field.type === 'array' ? (field.item?.type ?? 'any') : field.type;
  const fits = field.type === 'any' || typesAgree(wanted, given);
  const verdict = fits ? `fits ${field.name}` : `${field.name} takes a list of ${wanted}`;
  return { text: `Yields a list of ${given}: ${verdict}`, fits };
}

export function ListFilterEditor({
  field,
  source,
  references,
  itemsOf,
  onChange,
}: {
  field: FieldSpec;
  source: LinkageListFilter;
  references: readonly ReferenceOption[];
  /** What an item condition or a pick may read, for the list at a path. */
  itemsOf: (ref: string) => ReferenceOption[];
  onChange: (source: LinkageListFilter) => void;
}) {
  const name = field.name;
  // A list inside a list's items is never a filter's list (Feature 82 D12).
  const lists = references.filter((r) => !r.within && (r.type === 'array' || r.type === 'any'));
  const known = lists.some((r) => r.value === source.ref);
  const items = itemsOf(source.ref);
  const inner = source.where?.items ?? [];
  const join = source.where?.join ?? 'all';
  const setInner = (next: LinkageRule[]) => onChange(withWhere(source, next, join));
  // The first of the item's own fields, or the item itself when none is described.
  const firstField = items.find((o) => o.value !== ITEM)?.value ?? ITEM;
  const given = (source.pick ? items.find((o) => o.value === source.pick) : items[0])?.type;
  const result = yieldText(field, given ?? 'any');

  return (
    <Stack gap={7}>
      <Select
        searchable
        aria-label={`List of ${name}`}
        value={source.ref}
        onChange={(ref) => onChange({ ...source, ref })}
        panelWidth={PICKER_WIDTH}
        searchPlaceholder="Search a list…"
        options={[
          ...(known ? [] : [storedOption(source.ref, 'not a list upstream')]),
          ...pickerOptions(lists),
        ]}
      />
      <PositionHint path={source.ref} />
      <Field id={`filter-join-${name}`} label="Keep items where">
        <SegmentedControl
          items={[
            { value: 'all', label: 'All hold' },
            { value: 'any', label: 'Any holds' },
          ]}
          value={join}
          onChange={(next) => onChange(withWhere(source, inner, next as LinkageJoin))}
        />
      </Field>
      {inner.length === 0 && (
        <Typography variant="meta">Every item, until a condition is added.</Typography>
      )}
      {inner.map((rule, i) => (
        <RuleRow
          key={i}
          index={i}
          label={`${name} item ${i + 1}`}
          missing="not in the item"
          rule={rule}
          references={items}
          onChange={(next) => setInner(inner.map((r, j) => (j === i ? next : r)))}
          onRemove={() => setInner(inner.filter((_, j) => j !== i))}
        />
      ))}
      <Button
        variant="ghost"
        size="sm"
        icon="add"
        onClick={() => setInner([...inner, itemCondition()])}
      >
        Add an item condition
      </Button>
      <Field id={`filter-pick-${name}`} label="Hand on">
        <SegmentedControl
          items={[
            { value: 'whole', label: 'Whole items' },
            { value: 'field', label: 'One field' },
          ]}
          value={source.pick === undefined ? 'whole' : 'field'}
          onChange={(mode) => onChange(withPick(source, mode === 'field' ? firstField : undefined))}
        />
      </Field>
      {source.pick !== undefined && (
        <Select
          searchable
          aria-label={`Field of ${name}`}
          value={source.pick}
          onChange={(pick) => onChange(withPick(source, pick))}
          panelWidth={PICKER_WIDTH}
          searchPlaceholder="Search a field…"
          options={[
            ...(items.some((o) => o.value === source.pick)
              ? []
              : [storedOption(source.pick, 'not in the item')]),
            ...pickerOptions(items),
          ]}
        />
      )}
      <PositionHint path={source.pick} />
      {inner.length === 0 && source.pick === undefined ? (
        <Typography variant="meta">Keeps every item: the same as a reference.</Typography>
      ) : (
        <Typography variant="meta" style={result.fits ? {} : { color: 'var(--warn)' }}>
          {result.text}
        </Typography>
      )}
    </Stack>
  );
}
